import { AlertBanner, Skeleton } from '@curri/components'

import type { PayloadWebhookSummary } from '../../api/webhooks.ts'

import { describeFailure, formatSuccessRate } from './format.ts'
import { StatTile, TILE_GRID } from './StatTile.tsx'
import { usePayloadWebhookSummary } from './useWebhookData.ts'

// The payload pipeline's three numbers. No dead-endpoints tile, unlike the
// delivery summary: payload retries run per event on a bounded ladder and no
// endpoint is ever paused, so the API reports no exhausted state to count.

const Tiles = ({ summary }: { summary: PayloadWebhookSummary }) => {
  const nothingSent = summary.delivered + summary.failed === 0
  const windowHint = `last ${summary.windowDays} days`

  return (
    <div style={TILE_GRID}>
      <StatTile
        hint={windowHint}
        label="Delivered"
        value={summary.delivered.toLocaleString()}
      />
      <StatTile
        hint={windowHint}
        label="Failed"
        tone={summary.failed > 0 ? 'danger' : 'neutral'}
        value={summary.failed.toLocaleString()}
      />
      <StatTile
        hint={nothingSent ? 'nothing sent in this window' : windowHint}
        label="Success rate"
        value={formatSuccessRate(summary.successRate)}
      />
    </div>
  )
}

export const PayloadSummaryStats = ({ windowDays }: { windowDays: number }) => {
  const resource = usePayloadWebhookSummary(windowDays)

  if (resource.status === 'loading') {
    return (
      <div style={TILE_GRID}>
        {[0, 1, 2].map(index => (
          <Skeleton key={index} style={{ height: 112 }} />
        ))}
      </div>
    )
  }

  if (resource.status === 'failed') {
    const copy = describeFailure(resource.failure)
    return (
      // A failed summary must not take the attempts table down with it, so this
      // renders in place of the tiles and the rest of the screen carries on.
      <AlertBanner title={copy.title} type={copy.tone}>
        {copy.body}
      </AlertBanner>
    )
  }

  return <Tiles summary={resource.data} />
}
